import {
  Badge,
  Button,
  Grid,
  Group,
  Loader,
  MultiSelect,
  Paper,
  ScrollArea,
  Select,
  Stack,
  Table,
  Text,
} from "@mantine/core";
import { DatePickerInput } from "@mantine/dates";
import { Bar, Line, getElementAtEvent } from "react-chartjs-2";
import React from "react";
import {
  FaArrowDown,
  FaArrowUp,
  FaBolt,
  FaCalendarAlt,
  FaCheckCircle,
  FaChartBar,
  FaClock,
  FaExclamationCircle,
  FaFilter,
  FaLayerGroup,
} from "react-icons/fa";
import { useSearchParams } from "react-router-dom";
import AnalyticsMetricCard from "../../components/analytics/metric_card";
import PerformancePostsTable from "../../components/analytics/posts_table";
import ClientDetailDrawer from "../../components/analytics/client_detail_drawer";
import SimpleHeader from "../../components/simple_header";
import { useApp } from "../../contexts/app.context";
import { GET_PERFORMANCE_ANALYTICS } from "../../services/jobs";
import {
  PerformanceAnalyticsQuery,
  PerformanceAnalyticsResponse,
  PerformanceCustomerSummary,
  PerformanceDelayPeriodPoint,
  PerformanceLifecyclePeriodPoint,
  PerformancePeriod,
  PerformancePostAnalytics,
  WorkflowStatusKey,
} from "../../types/performance_analytics";
import {
  compareNullableNumbers,
  EmptyAnalyticsState,
  ErrorAnalyticsState,
  formatCount,
  formatDate,
  formatDuration,
  formatPercentage,
  PERIOD_OPTIONS,
  SectionPanel,
  STATUS_COLORS,
  STATUS_LABELS,
  UNAVAILABLE_LABEL,
} from "./shared";

const STATUS_KEYS = Object.keys(STATUS_LABELS) as WorkflowStatusKey[];

type SortDirection = "asc" | "desc";

const parseCustomerIds = (raw: string | null) => {
  return (raw || "")
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean);
};

const toNullableDate = (value?: string) => {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

const buildChartOptions = ({
  stacked = false,
  yTickFormatter,
}: {
  stacked?: boolean;
  yTickFormatter?: (value: number) => string;
}) => ({
  responsive: true,
  maintainAspectRatio: false,
  interaction: {
    mode: "index" as const,
    intersect: false,
  },
  plugins: {
    legend: {
      position: "top" as const,
    },
  },
  scales: {
    x: { stacked },
    y: {
      stacked,
      ticks: {
        callback: (value: any) => (yTickFormatter ? yTickFormatter(Number(value)) : `${value}`),
      },
    },
  },
  elements: {
    line: {
      tension: 0.28,
    },
  },
});

const buildSecondaryAxisOptions = () => ({
  responsive: true,
  maintainAspectRatio: false,
  interaction: {
    mode: "index" as const,
    intersect: false,
  },
  plugins: {
    legend: {
      position: "top" as const,
    },
  },
  scales: {
    y: {
      position: "left" as const,
      ticks: {
        callback: (value: any) => formatDuration(Number(value)),
      },
    },
    y1: {
      position: "right" as const,
      grid: { drawOnChartArea: false },
    },
  },
  elements: {
    line: {
      tension: 0.28,
    },
  },
});

const getLargestStatusTime = (customer: PerformanceCustomerSummary) => {
  return STATUS_KEYS.reduce(
    (largest, statusKey) => {
      const value = customer.timeInStatus.averageTimeMsByStatus?.[statusKey] ?? null;
      if ((value ?? -1) > (largest.value ?? -1)) {
        return { statusKey, value };
      }
      return largest;
    },
    { statusKey: STATUS_KEYS[0], value: customer.timeInStatus.averageTimeMsByStatus?.[STATUS_KEYS[0]] ?? null }
  );
};

export default function PerformanceAnalyticsPage() {
  const { customers } = useApp();
  const [searchParams, setSearchParams] = useSearchParams();
  const timeInStatusChartRef = React.useRef<any>(null);

  const [loading, setLoading] = React.useState(true);
  const [refreshToken, setRefreshToken] = React.useState(0);
  const [analytics, setAnalytics] = React.useState<PerformanceAnalyticsResponse | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  const [visibleCustomerIds, setVisibleCustomerIds] = React.useState<string[]>([]);
  const [delaySortDirection, setDelaySortDirection] = React.useState<SortDirection>("desc");

  const explicitCustomerId = searchParams.get("customerId") || undefined;
  const explicitCustomerIds = parseCustomerIds(searchParams.get("customerIds"));
  const explicitSelectedCustomerIds = explicitCustomerIds.length > 0
    ? explicitCustomerIds
    : explicitCustomerId
      ? [explicitCustomerId]
      : [];

  const detailCustomerId = searchParams.get("detailCustomerId") || undefined;
  const detailJobId = searchParams.get("detailJobId") || undefined;

  const query: PerformanceAnalyticsQuery = React.useMemo(() => ({
    dateStart: searchParams.get("dateStart") || undefined,
    dateEnd: searchParams.get("dateEnd") || undefined,
    period: (searchParams.get("period") as PerformancePeriod | null) || undefined,
    customerId: explicitCustomerIds.length === 0 ? explicitCustomerId : undefined,
    customerIds: explicitCustomerIds.length > 1 ? explicitCustomerIds.join(",") : undefined,
  }), [explicitCustomerId, explicitCustomerIds, searchParams]);

  const activeCustomers = React.useMemo(
    () => (customers || []).filter((customer) => customer?.active),
    [customers]
  );

  const customerOptions = React.useMemo(
    () => activeCustomers.map((customer) => ({ label: customer.name ?? "", value: customer._id })),
    [activeCustomers]
  );

  const requestKey = React.useMemo(() => searchParams.toString(), [searchParams.toString()]);

  const syncResponseFiltersToUrl = React.useCallback((response: PerformanceAnalyticsResponse) => {
    const next = new URLSearchParams(searchParams);
    let changed = false;

    if (!searchParams.get("dateStart") && response.filters.dateStart) {
      next.set("dateStart", response.filters.dateStart);
      changed = true;
    }
    if (!searchParams.get("dateEnd") && response.filters.dateEnd) {
      next.set("dateEnd", response.filters.dateEnd);
      changed = true;
    }
    if (!searchParams.get("period") && response.filters.period) {
      next.set("period", response.filters.period);
      changed = true;
    }
    if (!searchParams.get("customerId") && !searchParams.get("customerIds") && response.filters.customerIds?.length) {
      if (response.filters.customerIds.length === 1) {
        next.set("customerId", response.filters.customerIds[0]);
      } else {
        next.set("customerIds", response.filters.customerIds.join(","));
      }
      changed = true;
    }

    if (changed) {
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const loadAnalytics = React.useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await GET_PERFORMANCE_ANALYTICS(query);
      setAnalytics(response || null);
      syncResponseFiltersToUrl(response);
    } catch (err: any) {
      setError(err?.message || "Não foi possível carregar os analytics de performance.");
      setAnalytics(null);
    } finally {
      setLoading(false);
    }
  }, [query, syncResponseFiltersToUrl]);

  React.useEffect(() => {
    loadAnalytics();
  }, [refreshToken, requestKey]);

  React.useEffect(() => {
    // const availableIds = (analytics?.customers || []).map((customer) => customer.customerId);
    // if (availableIds.length === 0) {
    //   setVisibleCustomerIds([]);
    //   return;
    // }

    // const current = visibleCustomerIds.filter((id) => availableIds.includes(id));
    // if (current.length > 0) {
    //   return;
    // }

    setVisibleCustomerIds((analytics?.customers || []).map((customer) => customer.customerId ));
  }, [analytics?.customers]);

  React.useEffect(() => {
    if (!detailCustomerId || !analytics) return;

    const hasCustomer = analytics.customers.some((customer) => customer.customerId === detailCustomerId);
    if (!hasCustomer) {
      const next = new URLSearchParams(searchParams);
      next.delete("detailCustomerId");
      next.delete("detailJobId");
      setSearchParams(next, { replace: true });
    }
  }, [analytics, detailCustomerId, searchParams, setSearchParams]);

  const selectedDateRange = React.useMemo<[Date | null, Date | null]>(() => {
    const filters = analytics?.filters;
    return [toNullableDate(query.dateStart || filters?.dateStart), toNullableDate(query.dateEnd || filters?.dateEnd)];
  }, [analytics?.filters, query.dateEnd, query.dateStart]);

  const selectedPeriod = query.period || analytics?.filters.period || null;
  const selectedCustomerIds = explicitSelectedCustomerIds.length > 0
    ? explicitSelectedCustomerIds
    : analytics?.filters.customerIds || [];

  const filteredCustomers = React.useMemo(() => {
    const list = analytics?.customers || [];
    if (visibleCustomerIds.length === 0) {
      return list;
    }
    return list.filter((customer) => visibleCustomerIds.includes(customer.customerId));
  }, [analytics?.customers, visibleCustomerIds]);

  const focusedCustomer = filteredCustomers.length === 1 ? filteredCustomers[0] : null;

  const selectedCustomer = React.useMemo(
    () => analytics?.customers.find((customer) => customer.customerId === detailCustomerId) || null,
    [analytics?.customers, detailCustomerId]
  );

  const customerPosts = React.useMemo(() => {
    if (!selectedCustomer) return [];
    return (analytics?.posts || [])
      .filter((post) => post.customerId === selectedCustomer.customerId)
      .sort((left, right) => String(right.createdAt || "").localeCompare(String(left.createdAt || "")));
  }, [analytics?.posts, selectedCustomer]);

  const displayPosts = React.useMemo(() => {
    const posts = analytics?.posts || [];
    if (visibleCustomerIds.length === 0) {
      return posts;
    }
    return posts.filter((post) => visibleCustomerIds.includes(post.customerId));
  }, [analytics?.posts, visibleCustomerIds]);

  const lifecycleChartData = React.useMemo(() => ({
    labels: analytics?.charts.lifecycleOverTime.map((item) => item.label) || [],
    datasets: [
      {
        label: "Média geral",
        data: analytics?.charts.lifecycleOverTime.map((item) => item.averageLifecycleTimeMs) || [],
        borderColor: "#0f172a",
        backgroundColor: "rgba(15,23,42,0.08)",
      },
      ...filteredCustomers.map((customer) => ({
        label: customer.customerName,
        data: (analytics?.charts.lifecycleOverTime || []).map(
          (item: PerformanceLifecyclePeriodPoint) =>
            item.customers.find((entry) => entry.customerId === customer.customerId)?.averageLifecycleTimeMs ?? null
        ),
        borderColor: STATUS_COLORS[STATUS_KEYS[filteredCustomers.indexOf(customer) % STATUS_KEYS.length]],
        backgroundColor: "transparent",
      })),
    ],
  }), [analytics?.charts.lifecycleOverTime, filteredCustomers]);

  const timeInStatusChartData = React.useMemo(() => ({
    labels: filteredCustomers.map((customer) => customer.customerName),
    datasets: STATUS_KEYS.map((statusKey) => ({
      label: STATUS_LABELS[statusKey],
      data: filteredCustomers.map((customer) => customer.timeInStatus.averageTimeMsByStatus?.[statusKey] ?? null),
      backgroundColor: STATUS_COLORS[statusKey],
    })),
  }), [filteredCustomers]);

  const volumeChartData = React.useMemo(() => ({
    labels: analytics?.charts.volumeOverTime.map((item) => item.label) || [],
    datasets: [
      {
        label: "Criados",
        data: analytics?.charts.volumeOverTime.map((item) => item.createdPosts) || [],
        borderColor: "#2563eb",
        backgroundColor: "rgba(37,99,235,0.18)",
      },
      {
        label: "Publicados",
        data: analytics?.charts.volumeOverTime.map((item) => item.publishedPosts) || [],
        borderColor: "#16a34a",
        backgroundColor: "rgba(22,163,74,0.18)",
      },
      ...filteredCustomers.map((customer, index) => ({
        label: `${customer.customerName} publicados`,
        data: (analytics?.charts.volumeOverTime || []).map(
          (item) => item.customers.find((entry) => entry.customerId === customer.customerId)?.publishedPosts ?? null
        ),
        borderColor: ["#9333ea", "#ea580c", "#0891b2", "#be123c", "#4f46e5"][index % 5],
        hidden: filteredCustomers.length > 1,
      })),
    ],
  }), [analytics?.charts.volumeOverTime, filteredCustomers]);

  const throughputChartData = React.useMemo(() => ({
    labels: analytics?.charts.throughput.map((item) => item.label) || [],
    datasets: [
      {
        label: "Throughput total",
        data: analytics?.charts.throughput.map((item) => item.completedPosts) || [],
        borderColor: "#0f766e",
        backgroundColor: "rgba(15,118,110,0.18)",
      },
      ...filteredCustomers.map((customer, index) => ({
        label: customer.customerName,
        data: (analytics?.charts.throughput || []).map(
          (item) => item.customers.find((entry) => entry.customerId === customer.customerId)?.completedPosts ?? null
        ),
        borderColor: ["#2563eb", "#dc2626", "#7c3aed", "#0891b2", "#f59e0b"][index % 5],
      })),
    ],
  }), [analytics?.charts.throughput, filteredCustomers]);

  const revisionsChartData = React.useMemo(() => ({
    labels: filteredCustomers.map((customer) => customer.customerName),
    datasets: [
      {
        label: "Revisões médias",
        data: filteredCustomers.map((customer) => customer.approvalFlow.averageRevisionsPerPost),
        backgroundColor: "#3b82f6",
      },
      {
        label: "Ajustes médios",
        data: filteredCustomers.map((customer) => customer.approvalFlow.averageChangesRequestedPerPost),
        backgroundColor: "#f97316",
      },
    ],
  }), [filteredCustomers]);

  const approvalEfficiencyChartData = React.useMemo(() => ({
    labels: filteredCustomers.map((customer) => customer.customerName),
    datasets: [
      {
        label: "Aprovado sem ajustes",
        data: filteredCustomers.map((customer) => customer.approvalFlow.approvedWithoutChangesRate === null ? null : customer.approvalFlow.approvedWithoutChangesRate * 100),
        backgroundColor: "#22c55e",
      },
      {
        label: "Retrabalho",
        data: filteredCustomers.map((customer) => customer.rework.reworkRate === null ? null : customer.rework.reworkRate * 100),
        backgroundColor: "#ef4444",
      },
    ],
  }), [filteredCustomers]);

  const delayTrendChartData = React.useMemo(() => ({
    labels: analytics?.charts.delays.map((item) => item.label) || [],
    datasets: [
      {
        type: "line" as const,
        label: "Atraso médio",
        data: analytics?.charts.delays.map((item) => item.averageDelayMs) || [],
        borderColor: "#dc2626",
        backgroundColor: "rgba(220,38,38,0.18)",
        yAxisID: "y",
      },
      {
        type: "bar" as const,
        label: "Posts atrasados",
        data: analytics?.charts.delays.map((item) => item.delayedPosts) || [],
        backgroundColor: "rgba(249,115,22,0.5)",
        yAxisID: "y1",
      },
    ],
  }), [analytics?.charts.delays]);

  const bottleneckRanking = React.useMemo(() => {
    return [...filteredCustomers]
      .map((customer) => ({
        customer,
        largest: getLargestStatusTime(customer),
      }))
      .sort((left, right) => compareNullableNumbers(right.largest.value, left.largest.value));
  }, [filteredCustomers]);

  const delayTableRows = React.useMemo(() => {
    const rows = [...filteredCustomers];
    rows.sort((left, right) => {
      const result = compareNullableNumbers(left.delay.averageDelayMs, right.delay.averageDelayMs);
      return delaySortDirection === "asc" ? result : -result;
    });
    return rows;
  }, [delaySortDirection, filteredCustomers]);

  const approvalSummary = analytics?.summary;
  const comparisonMode = selectedCustomerIds.length > 1 || filteredCustomers.length > 1;

  const updateFilters = React.useCallback((updater: (params: URLSearchParams) => void) => {
    const next = new URLSearchParams(searchParams);
    updater(next);
    setSearchParams(next);
  }, [searchParams, setSearchParams]);

  const handleDateRangeChange = (value: [Date | null, Date | null]) => {
    updateFilters((next) => {
      const [start, end] = value;
      if (start) {
        next.set("dateStart", start.toISOString());
      } else {
        next.delete("dateStart");
      }

      if (end) {
        next.set("dateEnd", end.toISOString());
      } else {
        next.delete("dateEnd");
      }
    });
  };

  const handlePeriodChange = (value: string | null) => {
    updateFilters((next) => {
      if (value) {
        next.set("period", value);
      } else {
        next.delete("period");
      }
    });
  };

  const handleCustomerChange = (values: string[]) => {
    updateFilters((next) => {
      next.delete("customerId");
      next.delete("customerIds");

      if (values.length === 1) {
        next.set("customerId", values[0]);
      }

      if (values.length > 1) {
        next.set("customerIds", values.join(","));
      }
    });
  };

  const openCustomerDetail = React.useCallback((customerId: string, jobId?: string) => {
    updateFilters((next) => {
      next.set("detailCustomerId", customerId);
      if (jobId) {
        next.set("detailJobId", jobId);
      } else {
        next.delete("detailJobId");
      }
    });
  }, [updateFilters]);

  const closeCustomerDetail = React.useCallback(() => {
    updateFilters((next) => {
      next.delete("detailCustomerId");
      next.delete("detailJobId");
    });
  }, [updateFilters]);

  const handleSelectPost = React.useCallback((post: PerformancePostAnalytics) => {
    openCustomerDetail(post.customerId, post.jobId);
  }, [openCustomerDetail]);

  const handleRetry = React.useCallback(() => {
    setRefreshToken((current) => current + 1);
  }, []);

  const totalRangeLabel = analytics?.filters
    ? `${formatDate(analytics.filters.dateStart)} a ${formatDate(analytics.filters.dateEnd)}`
    : "";

  return (
    <Stack>
      <SimpleHeader
        title="Analytics de Performance"
        subtitle="Eficiência do workflow, gargalos, qualidade, agendamento e throughput por cliente."
        Icon={FaChartBar}
        right={
          <Group>
            <Badge color={comparisonMode ? "blue" : "teal"} variant="light">
              {comparisonMode ? "Modo comparação" : "Modo detalhe"}
            </Badge>
            <Button variant="light" leftIcon={<FaFilter />} onClick={handleRetry}>
              Atualizar
            </Button>
          </Group>
        }
      />

      <Paper withBorder p="md">
        <Grid align="end">
          <Grid.Col md={4}>
            <DatePickerInput
              type="range"
              label="Período"
              value={selectedDateRange}
              onChange={(value) => handleDateRangeChange(value as [Date | null, Date | null])}
              valueFormat="DD/MM/YYYY"
              allowSingleDateInRange
              clearable
            />
          </Grid.Col>
          <Grid.Col md={2}>
            <Select
              label="Granularidade"
              data={PERIOD_OPTIONS}
              value={selectedPeriod}
              onChange={handlePeriodChange}
              placeholder="Backend decide"
              clearable
            />
          </Grid.Col>
          <Grid.Col md={6}>
            <MultiSelect
              label="Clientes"
              data={customerOptions}
              value={selectedCustomerIds}
              onChange={handleCustomerChange}
              placeholder="Todos os clientes ativos"
              searchable
              clearable
            />
          </Grid.Col>
          <Grid.Col md={12}>
            <Group spacing="xs">
              <Badge color="gray" variant="light">
                Intervalo efetivo: {totalRangeLabel || UNAVAILABLE_LABEL}
              </Badge>
              <Badge color="gray" variant="light">
                {filteredCustomers.length} cliente(s) visível(is)
              </Badge>
            </Group>
          </Grid.Col>
        </Grid>
      </Paper>

      {loading ? (
        <Paper withBorder p="xl">
          <Group position="center">
            <Loader size="sm" />
            <Text c="dimmed">Carregando analytics de performance...</Text>
          </Group>
        </Paper>
      ) : error ? (
        <ErrorAnalyticsState message={error} onRetry={handleRetry} />
      ) : !analytics || analytics.customers.length === 0 ? (
        <EmptyAnalyticsState />
      ) : (
        <>
          <Grid>
            <Grid.Col md={3}>
              <AnalyticsMetricCard label="Posts criados" value={formatCount(approvalSummary?.volume.totalPostsCreated)} icon={<FaLayerGroup size={14} />} />
            </Grid.Col>
            <Grid.Col md={3}>
              <AnalyticsMetricCard label="Posts publicados" value={formatCount(approvalSummary?.volume.totalPostsPublished)} icon={<FaCheckCircle size={14} />} />
            </Grid.Col>
            <Grid.Col md={3}>
              <AnalyticsMetricCard label="Lifecycle médio" value={formatDuration(approvalSummary?.lifecycle.averageLifecycleTimeMs)} icon={<FaClock size={14} />} />
            </Grid.Col>
            <Grid.Col md={3}>
              <AnalyticsMetricCard label="Tempo médio de aprovação" value={formatDuration(approvalSummary?.approvalTime.averageApprovalTimeMs)} icon={<FaCalendarAlt size={14} />} />
            </Grid.Col>
            <Grid.Col md={4}>
              <AnalyticsMetricCard label="Taxa de retrabalho" value={formatPercentage(approvalSummary?.rework.reworkRate)} icon={<FaExclamationCircle size={14} />} />
            </Grid.Col>
            <Grid.Col md={4}>
              <AnalyticsMetricCard label="Taxa de posts atrasados" value={formatPercentage(approvalSummary?.delay.delayedPostsRate)} hint={`${formatCount(approvalSummary?.delay.delayedPostsCount)} posts`} icon={<FaArrowDown size={14} />} />
            </Grid.Col>
            <Grid.Col md={4}>
              <AnalyticsMetricCard label="Throughput" value={formatCount(approvalSummary?.throughput.totalCompletedPosts)} icon={<FaBolt size={14} />} />
            </Grid.Col>
          </Grid>

          {focusedCustomer ? (
            <SectionPanel
              title="Cliente em foco"
              description="Um único cliente está selecionado, então a leitura prioriza o detalhe operacional desse cliente."
              right={<Button variant="light" onClick={() => openCustomerDetail(focusedCustomer.customerId)}>Abrir detalhe</Button>}
            >
              <Grid>
                <Grid.Col md={3}>
                  <AnalyticsMetricCard label="Cliente" value={focusedCustomer.customerName} />
                </Grid.Col>
                <Grid.Col md={3}>
                  <AnalyticsMetricCard label="Aprovado sem ajustes" value={formatPercentage(focusedCustomer.approvalFlow.approvedWithoutChangesRate)} />
                </Grid.Col>
                <Grid.Col md={3}>
                  <AnalyticsMetricCard label="Aprovação até agendamento" value={formatDuration(focusedCustomer.scheduling.averageApprovalToScheduledTimeMs)} />
                </Grid.Col>
                <Grid.Col md={3}>
                  <AnalyticsMetricCard label="Agendamento até publicação" value={formatDuration(focusedCustomer.scheduling.averageScheduledToPublishedTimeMs)} />
                </Grid.Col>
              </Grid>
            </SectionPanel>
          ) : null}

          <Grid>
            <Grid.Col md={8}>
              <SectionPanel title="Eficiência do workflow" description="Tempo médio por status para cada cliente visível.">
                <div style={{ height: 360 }}>
                  <Bar
                    ref={timeInStatusChartRef}
                    data={timeInStatusChartData}
                    options={buildChartOptions({ stacked: true, yTickFormatter: (value) => formatDuration(value) })}
                    onClick={(event) => {
                      const elements = timeInStatusChartRef.current
                        ? getElementAtEvent(timeInStatusChartRef.current, event)
                        : [];
                      const index = elements?.[0]?.index;
                      if (index === undefined) return;
                      const customer = filteredCustomers[index];
                      if (customer) {
                        openCustomerDetail(customer.customerId);
                      }
                    }}
                  />
                </div>
              </SectionPanel>
            </Grid.Col>
            <Grid.Col md={4}>
              <SectionPanel title="Ranking de gargalos" description="Maior tempo médio por cliente e etapa predominante.">
                <Stack spacing="sm">
                  {bottleneckRanking.map(({ customer, largest }) => (
                    <Paper
                      withBorder
                      p="sm"
                      key={customer.customerId}
                      onClick={() => openCustomerDetail(customer.customerId)}
                      style={{ cursor: "pointer" }}
                    >
                      <Group position="apart" align="flex-start">
                        <div>
                          <Text fw={600}>{customer.customerName}</Text>
                          <Text size="sm" c="dimmed">
                            {STATUS_LABELS[largest.statusKey]} como maior estágio médio
                          </Text>
                        </div>
                        <Badge color="orange" variant="light">
                          {formatDuration(largest.value)}
                        </Badge>
                      </Group>
                    </Paper>
                  ))}
                </Stack>
              </SectionPanel>
            </Grid.Col>
          </Grid>

          <SectionPanel title="Tendência de lifecycle" description="Comparação entre média geral e clientes visíveis ao longo do tempo.">
            <div style={{ height: 340 }}>
              <Line data={lifecycleChartData} options={buildChartOptions({ yTickFormatter: (value) => formatDuration(value) })} />
            </div>
          </SectionPanel>

          <Grid>
            <Grid.Col md={8}>
              <SectionPanel title="Volume de conteúdo" description="Posts criados e publicados no período com leitura agregada do backend.">
                <div style={{ height: 320 }}>
                  <Line data={volumeChartData} options={buildChartOptions({})} />
                </div>
              </SectionPanel>
            </Grid.Col>
            <Grid.Col md={4}>
              <SectionPanel title="Throughput" description="Posts concluídos por período para análise de produtividade.">
                <div style={{ height: 320 }}>
                  <Line data={throughputChartData} options={buildChartOptions({})} />
                </div>
              </SectionPanel>
            </Grid.Col>
          </Grid>

          <Grid>
            <Grid.Col md={7}>
              <SectionPanel title="Qualidade da aprovação" description="Revisões, ajustes e eficiência de aprovação por cliente.">
                <div style={{ height: 320 }}>
                  <Bar data={revisionsChartData} options={buildChartOptions({})} />
                </div>
                <div style={{ height: 320, marginTop: 16 }}>
                  <Bar
                    data={approvalEfficiencyChartData}
                    options={buildChartOptions({ yTickFormatter: (value) => `${value.toFixed(0)}%` })}
                  />
                </div>
              </SectionPanel>
            </Grid.Col>
            <Grid.Col md={5}>
              <SectionPanel title="KPIs de aprovação" description="Leitura direta das métricas de qualidade e retrabalho.">
                <Grid>
                  <Grid.Col md={12}>
                    <AnalyticsMetricCard label="Revisões médias por post" value={approvalSummary?.approvalFlow.averageRevisionsPerPost === null || approvalSummary?.approvalFlow.averageRevisionsPerPost === undefined ? UNAVAILABLE_LABEL : approvalSummary.approvalFlow.averageRevisionsPerPost.toFixed(1)} />
                  </Grid.Col>
                  <Grid.Col md={12}>
                    <AnalyticsMetricCard label="Aprovado sem ajustes" value={formatPercentage(approvalSummary?.approvalFlow.approvedWithoutChangesRate)} />
                  </Grid.Col>
                  <Grid.Col md={12}>
                    <AnalyticsMetricCard label="Taxa de retrabalho" value={formatPercentage(approvalSummary?.rework.reworkRate)} />
                  </Grid.Col>
                </Grid>
              </SectionPanel>
            </Grid.Col>
          </Grid>

          <SectionPanel title="Agendamento e atrasos" description="Atrasos, cadência de agendamento e desempenho por cliente.">
            <Grid>
              <Grid.Col md={2.4}>
                <AnalyticsMetricCard label="Aprovação até agendamento" value={formatDuration(approvalSummary?.scheduling.averageApprovalToScheduledTimeMs)} />
              </Grid.Col>
              <Grid.Col md={2.4}>
                <AnalyticsMetricCard label="Agendamento até publicação" value={formatDuration(approvalSummary?.scheduling.averageScheduledToPublishedTimeMs)} />
              </Grid.Col>
              <Grid.Col md={2.4}>
                <AnalyticsMetricCard label="Posts atrasados" value={formatCount(approvalSummary?.delay.delayedPostsCount)} />
              </Grid.Col>
              <Grid.Col md={2.4}>
                <AnalyticsMetricCard label="Taxa de atraso" value={formatPercentage(approvalSummary?.delay.delayedPostsRate)} />
              </Grid.Col>
              <Grid.Col md={2.4}>
                <AnalyticsMetricCard label="Atraso médio" value={formatDuration(approvalSummary?.delay.averageDelayMs)} />
              </Grid.Col>
            </Grid>

            <Grid mt="sm">
              <Grid.Col md={8}>
                <div style={{ height: 320 }}>
                  <Bar data={delayTrendChartData as any} options={buildSecondaryAxisOptions() as any} />
                </div>
              </Grid.Col>
              <Grid.Col md={4}>
                <ScrollArea h={320}>
                  <Table striped highlightOnHover>
                    <thead>
                      <tr>
                        <th
                          style={{ cursor: "pointer" }}
                          onClick={() => setDelaySortDirection((current) => (current === "asc" ? "desc" : "asc"))}
                        >
                          Cliente {delaySortDirection === "asc" ? <FaArrowUp size={12} /> : <FaArrowDown size={12} />}
                        </th>
                        <th>Atraso médio</th>
                        <th>Taxa</th>
                      </tr>
                    </thead>
                    <tbody>
                      {delayTableRows.map((customer) => (
                        <tr key={customer.customerId} onClick={() => openCustomerDetail(customer.customerId)} style={{ cursor: "pointer" }}>
                          <td>{customer.customerName}</td>
                          <td>{formatDuration(customer.delay.averageDelayMs)}</td>
                          <td>{formatPercentage(customer.delay.delayedPostsRate)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </ScrollArea>
              </Grid.Col>
            </Grid>
          </SectionPanel>

          <SectionPanel title="Tabela de posts" description="Base detalhada para drilldown por item de workflow.">
            <PerformancePostsTable
              posts={displayPosts}
              onSelectPost={handleSelectPost}
              selectedJobId={detailJobId}
              showCustomer={!focusedCustomer}
            />
          </SectionPanel>

          <ClientDetailDrawer
            opened={!!selectedCustomer}
            onClose={closeCustomerDetail}
            customer={selectedCustomer}
            posts={customerPosts}
            lifecycleSeries={analytics.charts.lifecycleOverTime}
            delaySeries={analytics.charts.delays as PerformanceDelayPeriodPoint[]}
            throughputSeries={analytics.charts.throughput}
            selectedJobId={detailJobId}
            onSelectPost={handleSelectPost}
          />
        </>
      )}
    </Stack>
  );
}